import React from 'react';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { StyledScrollArea } from '@components/main-components/containers/RadixScroll';
import { SPACE } from '@utils/constants';

export function formatTierNumber(tierNumber: number | null): string {
  let editionLabel = '';
  if (tierNumber === 0) {
    editionLabel = `Forever${SPACE}`;
  } else if (tierNumber === 1) {
    editionLabel = `Limited${SPACE}`;
  }

  return editionLabel;
}

export const CARD_CONTAINER_CSS: CSS = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  borderRadius: 8,
  width: '100%',
  overflow: 'hidden',

  '@media (min-width: 400px)': {
    justifyContent: 'flex-start',
    height: 343,
  },

  '@tabletLandscape': {
    marginLeft: 65,
    maxWidth: `calc(100% - 65px)`,
  },

  '&:hover': {
    cursor: 'pointer',
  },
};

export const ComponentContainer = styled('div', {
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  gap: 16,

  '@desktop': {
    paddingX: 0,
  },

  variants: {
    noPadding: {
      true: {
        paddingX: 0,
      },
    },
  },
});

export const ComponentWrapper = styled('div', {
  display: 'flex',
  position: 'relative',
  alignItems: 'flex-end',
  width: '-webkit-fill-available',
  marginLeft: 0,
  '@tabletLandscape': {
    marginLeft: 65,
  },

  variants: {
    onlyMargin: {
      true: {
        display: 'unset',
        width: 'unset',
        '@tabletLandscape': {
          marginLeft: 65,
        },
      },
    },
  },
});

export const FeaturedCollectorContainer = styled('div', {
  display: 'flex',
});

export const PaddingContainer = styled('div', {
  '@desktop': {
    paddingX: 0,
  },
});

const Format = styled('div', {
  display: 'flex',
  width: 'max-content',
  height: 'max-content',
  alignItems: 'center',
});

export type DisplayConjunctionItem = { id: string; element: React.ReactNode };

/**
 * Formats a conjunction of items
 * @param items
 * @param css
 * @returns JSX
 * @note The output always has a trailing space
 */
export const formatConjunction = ({
  items,
  css = {},
}: {
  items: DisplayConjunctionItem[];
  css?: CSS;
}) => {
  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return items.map(({ element }) => (
      <>
        {element}
        {SPACE}
      </>
    ));
  }

  const formattedItems = items.map((item, index) => {
    const last = index === items.length - 1;
    const onlyTwo = items.length === 2;

    return (
      <Format key={item.id} css={css}>
        {last && <span>{`and${SPACE}`}</span>}
        {item.element}
        {(onlyTwo || last) && SPACE}
        {!last && !onlyTwo && <span>,{SPACE}</span>}
      </Format>
    );
  });

  return formattedItems;
};

export const ChipContainer = styled('div', {
  width: 'fit-content',
  marginX: 4,
  paddingY: 8,

  '&:first-child': {
    marginLeft: 0,
  },
  '&:last-child': {
    marginRight: '2rem',
  },
  '@tablet': {
    paddingY: 0,
  },
});

export const RestyledScrollArea = styled(StyledScrollArea, {
  width: 'unset',
});
